// Écrans de la station (E5, maquettes S01 et S02, charte D-038) : accueil à quatre actions, exceptions d'abord ;
// déclaration de stock et de statut en moins de deux minutes ; demande de réapprovisionnement ; signalement
// d'incident et de rupture ; horaires ; historique. Les saisies de terrain passent par la file hors connexion :
// « enregistré sur cet appareil » puis « synchronisé » seulement à l'accusé du serveur.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre, formaterQuantite, PRODUITS_INITIAUX, type Unite } from '@carburanttrack/shared';
import { JOURS_SEMAINE, LIBELLES_TYPES_INCIDENT_STATION, TYPES_INCIDENT_STATION, TYPES_OPERATIONS_LOT3, URGENCES_BESOIN, deProduit, type TypeIncidentStation } from '@carburanttrack/shared/logistique';
import { LIBELLES_MOTIF_NON_DISTRIBUABLE, LIBELLES_STATUT_STATION, MOTIFS_NON_DISTRIBUABLE, STATUTS_STATION } from '@carburanttrack/shared/matiere';
import { api, messageErreur } from '../../api/client';
import { useSession } from '../../app/session';
import { Indisponible, Message, MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { date, dateHeure, libelleProvince } from '../../composants/format';
import { Cuve } from '../../composants/Visualisations';
import { useHorsLigne } from '../../horsligne/contexte';
import { LIBELLES_ETATS_OPERATION } from '../../horsligne/file';
import { AutonomieJours, LienAction } from '../stocks/composants';
import { AfficherPlus, libelleEtatBesoin, libelleUrgence, quantite, usePaquet } from './communs';
import type { AccueilStation, DroitsStation, PaquetTerrain } from './types';

export const nomProduit = (code: string) => PRODUITS_INITIAUX.find((p) => p.code === code)?.nom ?? code.replace(/_/g, ' ');

export interface StationGeree {
  id: string;
  nom: string;
  provinceCode: string | null;
  gestion: boolean;
  droits: DroitsStation;
}

/** Hors connexion, le paquet de travail ne contient que les stations que l'utilisateur exploite ; le serveur
 * applique de toute façon ses règles à l'envoi. */
const DROITS_EXPLOITANT: DroitsStation = { declarer: true, signaler: true, demander: true, receptionner: true, horaires: true };

/** Stations suivies par l'utilisateur : par l'API en ligne, par le paquet de travail hors connexion. */
export function useMesStations() {
  const { etat } = useSession();
  const paquet = usePaquet();
  const enLigne = !etat?.horsLigne;
  const requete = useQuery({
    queryKey: ['stations', 'mes-stations'],
    queryFn: async () => (await api<StationGeree[]>('/api/v1/stations/mes-stations')).donnees,
    enabled: enLigne,
    retry: false,
  });
  const horsLigne = (paquet.data?.contenu.stations ?? []).map((s) => ({ id: s.id, nom: s.nom, provinceCode: s.provinceCode, gestion: true, droits: DROITS_EXPLOITANT }));
  const stations = requete.data ?? (requete.isError || !enLigne ? horsLigne : undefined);
  return { stations, chargement: (enLigne && requete.isLoading) || paquet.isLoading, erreur: requete.data || horsLigne.length ? null : requete.error, paquet: paquet.data?.contenu ?? null, paquetLe: paquet.data?.enregistreLe ?? null };
}

function useStationCourante() {
  const [params] = useSearchParams();
  const m = useMesStations();
  const choisie = params.get('station');
  const station = m.stations?.find((s) => s.id === choisie) ?? m.stations?.find((s) => s.gestion) ?? m.stations?.[0] ?? null;
  return { ...m, station };
}

function SelecteurStation({ stations, courante }: { stations: StationGeree[]; courante: string }) {
  const navigate = useNavigate();
  if (stations.length < 2) return null;
  return (
    <div className="champ champ-etroit">
      <label htmlFor="choix-station">Station</label>
      <select id="choix-station" value={courante} onChange={(e) => navigate(`?station=${e.target.value}`)}>
        {stations.map((s) => (
          <option key={s.id} value={s.id}>
            {s.nom}
          </option>
        ))}
      </select>
    </div>
  );
}

// --- Accueil -----------------------------------------------------------------------------------------------

export function PageAccueilStation() {
  const { etat } = useSession();
  const { stations, station, chargement, erreur, paquet, paquetLe } = useStationCourante();
  const accueil = useQuery({
    queryKey: ['station', station?.id, 'accueil'],
    queryFn: async () => (await api<AccueilStation>(`/api/v1/stations/${station!.id}/accueil`)).donnees,
    enabled: Boolean(station) && !etat?.horsLigne,
    retry: false,
  });
  if (chargement) return <Squelette lignes={6} libelle="Chargement de votre station" />;
  if (erreur) return <MessageErreur erreur={erreur} titre="Stations indisponibles" />;
  if (!station || !stations) return <Vide>Aucune station n’est rattachée à votre affectation.</Vide>;
  if (accueil.isLoading) return <Squelette lignes={6} libelle="Chargement de votre station" />;
  if (accueil.data) return <AccueilEnLigne a={accueil.data} stations={stations} />;
  return <AccueilHorsLigne stationId={station.id} stations={stations} paquet={paquet} paquetLe={paquetLe} erreur={accueil.error} />;
}

/** Actions de la station : seules celles que le serveur accepterait pour ce compte sont proposées (un lecteur
 * « restreinte », autorité provinciale ou inspecteur, n'en a aucune et le bloc n'apparaît pas). */
function QuatreActions({ stationId, livraisons, conseil, droits }: { stationId: string; livraisons: number; conseil: string | null; droits: DroitsStation }) {
  const q = `?station=${stationId}`;
  if (!droits.declarer && !droits.receptionner && !droits.demander && !droits.signaler) return null;
  return (
    <section className="carte-bloc" aria-labelledby="titre-agir">
      <h2 id="titre-agir">Agir</h2>
      <ul className="liste-actions">
        {droits.declarer && <LienAction vers={`/declarer${q}`} icone="formulaire" titre="Déclarer les stocks" detail="Relevé du jour et statut de vente, moins de deux minutes" />}
        {droits.receptionner && <LienAction vers={`/livraisons${q}`} icone="camion" titre="Confirmer une livraison" detail={livraisons ? `${livraisons} attendue${livraisons > 1 ? 's' : ''}` : 'Aucune livraison attendue'} />}
        {droits.demander && <LienAction vers={`/station/demande${q}`} icone="ajout" titre="Demander un réapprovisionnement" detail={conseil ?? 'Besoin adressé à votre distributeur'} />}
        {droits.signaler && <LienAction vers={`/station/incident${q}`} icone="incident" titre="Signaler un incident" detail="Pompe, rupture, fermeture, sécurité" />}
      </ul>
    </section>
  );
}

/** Page d'action ouverte par un compte sans ce droit : aucun formulaire ni bouton, le motif et le retour. */
function ActionReservee({ station, stations, titre, motif }: { station: StationGeree; stations: StationGeree[]; titre: string; motif: string }) {
  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="attenue">{station.nom}</span>
          <h1>{titre}</h1>
        </div>
        <SelecteurStation stations={stations} courante={station.id} />
      </div>
      <Message type="info">{motif} Votre affectation vous donne la lecture de cette station, sans action sur son exploitation.</Message>
      <div className="actions">
        <Link className="bouton" to={`/?station=${station.id}`}>
          Retour à la station
        </Link>
      </div>
    </div>
  );
}

/** Volume commercial de l'accueil : masqué par le serveur pour les lecteurs sans droit sur les ventes (motif affiché, jamais zéro). */
const volumeAccueil = (valeur: string | null, a: Pick<AccueilStation, 'motifVolumesMasques'>) => (valeur === null ? `indisponible (${a.motifVolumesMasques ?? 'volume non lisible'})` : quantite(valeur, 'L'));

export function AccueilEnLigne({ a, stations }: { a: AccueilStation; stations: StationGeree[] }) {
  const { etat } = useHorsLigne();
  const enAttente = etat.compteurs.enregistre_local + etat.compteurs.en_attente_envoi + etat.compteurs.envoye;
  const manquee = a.echeances.find((e) => e.manquees > 0);
  const arrivees = a.livraisonsAttendues.filter((l) => l.etat === 'arrive' || l.etat === 'recu_partiellement');
  const ruptures = a.produits.filter((p) => p.statut === 'rupture_confirmee');
  const faible = a.produits.filter((p) => p.autonomie?.jours !== null && p.autonomie?.jours !== undefined && Number(p.autonomie.jours) < 3).sort((x, y) => Number(x.autonomie!.jours) - Number(y.autonomie!.jours))[0];
  // Exploitant (gérant, distributeur) : exceptions avec leur action, textes à la première personne. Lecteur sans
  // droit d'action (autorité provinciale, inspecteur) : les mêmes situations, décrites à la troisième personne,
  // sans bouton (le serveur refuserait l'action).
  const exploitant = a.droits.declarer || a.droits.signaler || a.droits.demander || a.droits.receptionner;
  const echeances = manquee ? `${manquee.manquees} échéance${manquee.manquees > 1 ? 's' : ''} manquée${manquee.manquees > 1 ? 's' : ''}. ` : '';
  const exceptions: { ton: 'rouge' | 'ambre'; titre: string; texte: string; vers: string | null; action: string | null }[] = [];
  if (a.informationNonActualisee || manquee)
    exceptions.push(
      a.droits.declarer
        ? {
            ton: 'ambre',
            titre: a.derniereDeclarationStatut ? 'Votre déclaration n’est plus à jour' : 'Aucune déclaration reçue pour votre station',
            texte: `${echeances}Tant que vos cuves ne sont pas déclarées, le ministère voit votre station comme « information non actualisée ». Le relevé prend environ deux minutes.`,
            vers: `/declarer?station=${a.station.id}`,
            action: 'Déclarer mes stocks',
          }
        : {
            ton: 'ambre',
            titre: a.derniereDeclarationStatut ? 'Déclaration de la station plus à jour' : 'Aucune déclaration reçue de la station',
            texte: `${echeances}Tant que ses cuves ne sont pas déclarées, la station est vue comme « information non actualisée ».`,
            vers: null,
            action: null,
          },
    );
  for (const l of arrivees)
    exceptions.push({
      ton: 'ambre',
      titre: a.droits.receptionner ? `Livraison ${l.numero} à confirmer` : `Livraison ${l.numero} en attente de confirmation`,
      texte: `${volumeAccueil(l.reliquat ?? l.volume, a)} ${deProduit(l.produitCode)}${l.reliquat ? ' (reliquat d’une réception partielle)' : ', arrivée annoncée par le conducteur'}.`,
      vers: a.droits.receptionner ? `/livraisons/${l.id}/reception` : null,
      action: a.droits.receptionner ? 'Confirmer la réception' : null,
    });
  for (const p of ruptures)
    exceptions.push({
      ton: 'rouge',
      titre: `Rupture ${deProduit(p.produit)}`,
      texte: a.droits.demander ? 'Rupture confirmée : demandez un réapprovisionnement à votre distributeur si ce n’est pas déjà fait.' : 'Rupture confirmée par la station.',
      vers: a.droits.demander ? `/station/demande?station=${a.station.id}&produit=${p.produit}` : null,
      action: a.droits.demander ? 'Demander une livraison' : null,
    });
  const prochaine = a.livraisonsAttendues.find((l) => l.etat !== 'emis') ?? a.livraisonsAttendues[0];
  return (
    <div>
      <div className="station-tete">
        <div>
          <span className="attenue">
            {a.station.organisationNom}, {libelleProvince(a.station.provinceCode)}
          </span>
          <h1>{a.station.nom}</h1>
          <div className="station-etat">
            {a.statutStation ? <span className={`etiquette ${a.statutStation === 'ouverte' ? '' : 'ambre'}`}>Station {LIBELLES_STATUT_STATION[a.statutStation as keyof typeof LIBELLES_STATUT_STATION]?.toLowerCase() ?? a.statutStation}</span> : <Indisponible motif="aucune déclaration de statut" />}
            {a.informationNonActualisee && <span className="etiquette ambre">Information non actualisée</span>}
            <span className="attenue">Dernière déclaration : {a.derniereDeclarationStatut ? dateHeure(a.derniereDeclarationStatut) : 'aucune'}</span>
          </div>
        </div>
        <SelecteurStation stations={stations} courante={a.station.id} />
      </div>

      {enAttente > 0 && (
        <Message type="info">
          {enAttente} enregistrement{enAttente > 1 ? 's' : ''} sur cet appareil : {LIBELLES_ETATS_OPERATION.en_attente_envoi.toLowerCase()}. <Link to="/synchronisation">Voir le détail</Link>
        </Message>
      )}

      {exceptions.slice(0, 3).map((e) => (
        <section key={e.titre} className={`a-faire ${e.ton === 'rouge' ? 'rouge' : ''}`} aria-label={e.titre}>
          <div>
            <span className="sur-titre">{e.action ? 'À faire maintenant' : 'Situation à suivre'}</span>
            <h2>{e.titre}</h2>
            <p>{e.texte}</p>
          </div>
          {e.vers && e.action && (
            <Link className="bouton primaire" to={e.vers}>
              {e.action}
            </Link>
          )}
        </section>
      ))}

      <div className="station-grille">
        <div>
          <section className="carte-bloc" aria-labelledby="titre-produits">
            <div className="carte-bloc-tete">
              <h2 id="titre-produits">{exploitant ? 'Ma station' : 'Station'}</h2>
              <span className="attenue">Stocks déclarés {a.derniereDeclarationStock ? dateHeure(a.derniereDeclarationStock) : 'jamais'}</span>
            </div>
            <ul className="liste-lignes">
              {a.produits.map((p) => (
                <li key={p.produit}>
                  <div>
                    <b>{nomProduit(p.produit)}</b>
                    <small className="attenue">
                      {a.volumesLisibles ? (p.stockUtilisable !== null ? `${quantite(p.stockUtilisable, p.unite)} utilisables sur ${quantite(p.capacite, p.unite)}` : 'stock indisponible') : `capacité ${quantite(p.capacite, p.unite)}`}
                      {p.age ? `, relevé ${p.age}` : ''}
                    </small>
                  </div>
                  <span className={`etiquette ${p.statut === 'rupture_confirmee' ? 'rouge' : p.statut === 'disponible' ? '' : 'ambre'}`}>
                    {p.statutLibelle}
                    {p.motif ? ` (${LIBELLES_MOTIF_NON_DISTRIBUABLE[p.motif as keyof typeof LIBELLES_MOTIF_NON_DISTRIBUABLE]?.toLowerCase() ?? p.motif})` : ''}
                  </span>
                  {p.autonomie ? <AutonomieJours jours={p.autonomie.jours} confiance={p.autonomie.confiance} motif={p.autonomie.motif ?? 'motif non précisé'} compact ton={faible?.produit === p.produit ? 'ambre' : null} /> : <Indisponible motif="autonomie non calculée" />}
                </li>
              ))}
            </ul>
          </section>
          {a.volumesLisibles && a.cuves.length > 0 && (
            <section className="carte-bloc" aria-labelledby="titre-cuves">
              <h2 id="titre-cuves">{exploitant ? 'Mes cuves' : 'Cuves'}</h2>
              <div className="cuves compactes">
                {a.cuves.map((c) => (
                  <Cuve key={c.id} produit={`${c.numero}, ${nomProduit(c.produit)}`} capacite={c.capacite} unite={(c.unite === 'L' ? 'L' : c.unite) as Unite} volume={c.volume} motifIndisponible="aucun relevé reçu" age={c.horodatage ? dateHeure(c.horodatage) : null} />
                ))}
              </div>
            </section>
          )}
        </div>
        <aside className="station-cote">
          <QuatreActions stationId={a.station.id} livraisons={a.livraisonsAttendues.length} conseil={faible ? `Autonomie ${deProduit(faible.produit)} sous 3 jours` : null} droits={a.droits} />
          <section className="carte-bloc" aria-labelledby="titre-prochaine">
            <h2 id="titre-prochaine">Prochaine livraison</h2>
            {prochaine ? (
              <p>
                <Link to={`/livraisons/${prochaine.id}`}>
                  <b>{prochaine.numero}</b>
                </Link>
                , {volumeAccueil(prochaine.volume, a)} {deProduit(prochaine.produitCode)}
                <br />
                <span className="attenue">
                  {prochaine.etatLibelle}, arrivée prévue {dateHeure(prochaine.arriveePrevue)}
                </span>
              </p>
            ) : (
              <p className="attenue">Aucune livraison attendue. {a.dernierReapprovisionnement ? `Dernier réapprovisionnement : ${a.dernierReapprovisionnement.numero}, ${date(a.dernierReapprovisionnement.le)}.` : ''}</p>
            )}
          </section>
          {a.demandesEnCours.length > 0 && (
            <section className="carte-bloc" aria-labelledby="titre-demandes">
              <h2 id="titre-demandes">{exploitant ? 'Mes demandes en cours' : 'Demandes en cours'}</h2>
              <ul className="liste-lignes">
                {a.demandesEnCours.map((d) => (
                  <li key={d.id}>
                    <span>
                      <b>{d.reference}</b> {volumeAccueil(d.quantite, a)} {deProduit(d.produitCode)}
                    </span>
                    <span className={`etiquette ${d.urgence === 'normale' ? '' : 'ambre'}`}>{libelleEtatBesoin(d.etat)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {a.incidentsOuverts.length > 0 && (
            <section className="carte-bloc" aria-labelledby="titre-incidents">
              <h2 id="titre-incidents">Incidents ouverts</h2>
              <ul className="liste-lignes">
                {a.incidentsOuverts.map((i) => (
                  <li key={i.id}>
                    <span>
                      <b>{i.reference}</b> {LIBELLES_TYPES_INCIDENT_STATION[i.type as TypeIncidentStation]?.toLowerCase() ?? i.type}, {date(i.survenuLe)}
                    </span>
                    <span className="etiquette ambre">Gravité {i.gravite} sur 4</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="carte-bloc" aria-labelledby="titre-autres">
            <h2 id="titre-autres" className="sur-titre">
              Autres actions
            </h2>
            <ul className="liste-actions">
              <LienAction vers={`/station/horaires?station=${a.station.id}`} icone="liste" titre={a.droits.horaires ? 'Horaires' : 'Horaires (consultation)'} detail={a.horaires.definis ? `Version ${a.horaires.version}, modifiés ${date(a.horaires.modifieLe)}` : 'Horaires non renseignés'} />
              <LienAction vers={`/station/historique?station=${a.station.id}`} icone="document" titre="Historique" detail="Déclarations, livraisons, demandes, incidents" />
              <LienAction vers={`/documents?objetId=${a.station.id}`} icone="document" titre="Documents de la station" detail="Agréments, contrôles, contrats" />
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function AccueilHorsLigne({ stationId, stations, paquet, paquetLe, erreur }: { stationId: string; stations: StationGeree[]; paquet: PaquetTerrain | null; paquetLe: string | null; erreur: unknown }) {
  const s = paquet?.stations?.find((x) => x.id === stationId);
  const livraisons = (paquet?.livraisonsAttendues ?? []).filter((l) => l.destinationId === stationId);
  const demandes = (paquet?.demandes ?? []).filter((d) => d.installationId === stationId);
  if (!s) return <MessageErreur erreur={erreur ?? new Error('Données de la station absentes de cet appareil')} titre="Station indisponible hors connexion" />;
  return (
    <div>
      <div className="station-tete">
        <div>
          <span className="attenue">Dernières données synchronisées {paquetLe ? dateHeure(paquetLe) : 'à une date inconnue'}</span>
          <h1>{s.nom}</h1>
          <div className="station-etat">
            {s.dernierStatut ? <span className="etiquette">Station {LIBELLES_STATUT_STATION[s.dernierStatut.statutStation as keyof typeof LIBELLES_STATUT_STATION]?.toLowerCase()}</span> : <Indisponible motif="aucune déclaration de statut" />}
            <span className="attenue">Dernière déclaration connue : {s.dernierStatut ? dateHeure(s.dernierStatut.horodatage) : 'aucune'}</span>
          </div>
        </div>
        <SelecteurStation stations={stations} courante={stationId} />
      </div>
      <Message type="avertissement">Hors connexion : vos saisies sont enregistrées sur cet appareil, chiffrées, et partiront au retour du réseau. Les stocks affichés sont ceux de votre dernière déclaration connue.</Message>
      <div className="station-grille">
        <section className="carte-bloc" aria-labelledby="titre-statuts-locaux">
          <h2 id="titre-statuts-locaux">Statut par produit (dernière déclaration)</h2>
          <ul className="liste-lignes">
            {s.produits.map((p) => {
              const st = s.dernierStatut?.produits.find((x) => x.produit === p);
              return (
                <li key={p}>
                  <b>{nomProduit(p)}</b>
                  <span className="etiquette">{st ? st.statut.replace(/_/g, ' ') : 'non déclaré'}</span>
                </li>
              );
            })}
          </ul>
          {demandes.length > 0 && (
            <>
              <h3>Demandes en cours</h3>
              <ul className="liste-lignes">
                {demandes.map((d) => (
                  <li key={d.id}>
                    {d.reference} : {quantite(d.quantite, 'L')} {deProduit(d.produitCode)}, {libelleEtatBesoin(d.etat).toLowerCase()}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
        <aside className="station-cote">
          <QuatreActions stationId={stationId} livraisons={livraisons.length} conseil={null} droits={stations.find((x) => x.id === stationId)?.droits ?? DROITS_EXPLOITANT} />
        </aside>
      </div>
    </div>
  );
}

// --- Déclaration de stock et de statut -----------------------------------------------------------------------

export function PageDeclarer() {
  const { station, stations, paquet, chargement, erreur } = useStationCourante();
  const { synchroniseur } = useHorsLigne();
  const client = useQueryClient();
  const installation = paquet?.installationsDeclarables?.find((i) => i.id === station?.id);
  const stationPaquet = paquet?.stations?.find((s) => s.id === station?.id);
  const produits = stationPaquet?.produits ?? [...new Set(installation?.reservoirs.map((r) => r.produitCode) ?? [])];
  const [statutStation, setStatutStation] = useState<string>('ouverte');
  const [motifStation, setMotifStation] = useState('');
  const [statuts, setStatuts] = useState<Record<string, { statut: string; motif: string }>>({});
  const [volumes, setVolumes] = useState<Record<string, string>>({});
  const [quand, setQuand] = useState(() => new Date(Date.now() + 3_600_000).toISOString().slice(0, 16));
  const [resultat, setResultat] = useState<string | null>(null);
  useEffect(() => {
    if (stationPaquet?.dernierStatut) {
      setStatutStation(stationPaquet.dernierStatut.statutStation);
      setStatuts(Object.fromEntries(stationPaquet.dernierStatut.produits.map((p) => [p.produit, { statut: p.statut, motif: p.motif ?? '' }])));
    }
  }, [stationPaquet?.dernierStatut]);
  const envoi = useMutation({
    mutationFn: async () => {
      if (!station) throw new Error('Aucune station');
      const horodatageEvenement = new Date(`${quand}:00+01:00`);
      const saisis = (installation?.reservoirs ?? []).filter((r) => (volumes[r.id] ?? '').trim() !== '');
      for (const r of saisis) {
        await synchroniseur.enregistrer(
          'declaration_stock',
          { installationId: station.id, reservoirId: r.id, volumeBrut: volumes[r.id]!.replace(/\s/g, '').replace(',', '.'), methode: 'jauge_manuelle' },
          { horodatageEvenement, versionObjet: r.derniereDeclaration?.id ?? null, resume: `Relevé de la cuve ${r.numero} (${nomProduit(r.produitCode).toLowerCase()}), ${station.nom}` },
        );
      }
      await synchroniseur.enregistrer(
        'declaration_statut_station',
        {
          installationId: station.id,
          statutStation,
          motifStation: statutStation === 'ouverte' ? null : motifStation.trim() || null,
          produits: produits.map((p) => ({ produit: p, statut: statuts[p]?.statut ?? 'disponible', motif: statuts[p]?.statut === 'non_distribuable' ? statuts[p]?.motif || 'autre' : null })),
          cuves: [],
        },
        { horodatageEvenement, versionObjet: stationPaquet?.version ?? null, resume: `Statut de la ${station.nom}` },
      );
      return saisis.length;
    },
    onSuccess: (n) => {
      setResultat(`Déclaration enregistrée sur cet appareil (${n} cuve${n > 1 ? 's' : ''} et le statut de vente). Elle est envoyée dès que le réseau le permet ; « synchronisé » s’affichera à l’accusé du serveur.`);
      setVolumes({});
      void client.invalidateQueries({ queryKey: ['station'] });
    },
  });
  if (chargement) return <Squelette lignes={5} />;
  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!station || !stations) return <Vide>Aucune station n’est rattachée à votre affectation.</Vide>;
  if (!station.droits.declarer) return <ActionReservee station={station} stations={stations} titre="Déclarer les stocks" motif="La déclaration des stocks et du statut de vente est réservée à l’exploitant de la station." />;
  if (!installation) return <Message type="avertissement">Les cuves de cette station ne figurent pas dans les données de cet appareil : connectez-vous une fois au réseau pour les charger.</Message>;
  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    setResultat(null);
    envoi.mutate();
  };
  const incomplet = statutStation !== 'ouverte' && motifStation.trim().length < 3;
  return (
    <form onSubmit={soumettre} aria-labelledby="titre-declarer">
      <div className="page-tete">
        <div>
          <span className="attenue">{station.nom}</span>
          <h1 id="titre-declarer">Déclarer les stocks</h1>
          <p className="attenue">Relevé du jour et statut de vente de chaque produit. Un stock positif avec une pompe hors service se déclare « non distribuable ».</p>
        </div>
        <SelecteurStation stations={stations} courante={station.id} />
      </div>
      <fieldset className="carte-bloc">
        <legend>1. Statut de la station</legend>
        <div className="segments" role="radiogroup" aria-label="Statut de la station">
          {STATUTS_STATION.map((s) => (
            <button key={s} type="button" role="radio" aria-checked={statutStation === s} className={statutStation === s ? 'actif' : ''} onClick={() => setStatutStation(s)}>
              {LIBELLES_STATUT_STATION[s]}
            </button>
          ))}
        </div>
        {statutStation !== 'ouverte' && (
          <div className="champ">
            <label htmlFor="d-motif-station">Motif de la fermeture</label>
            <input id="d-motif-station" value={motifStation} onChange={(e) => setMotifStation(e.target.value)} required minLength={3} maxLength={500} />
          </div>
        )}
      </fieldset>
      <fieldset className="carte-bloc">
        <legend>2. Relevé des cuves</legend>
        <div className="formulaire-ligne">
        {installation.reservoirs.map((r) => (
          <div className="champ" key={r.id}>
            <label htmlFor={`d-cuve-${r.id}`}>
              Cuve {r.numero}, {nomProduit(r.produitCode)} ({r.unite})
            </label>
            <input id={`d-cuve-${r.id}`} inputMode="decimal" value={volumes[r.id] ?? ''} onChange={(e) => setVolumes({ ...volumes, [r.id]: e.target.value })} aria-describedby={`d-aide-${r.id}`} />
            <span className="aide" id={`d-aide-${r.id}`}>
              Capacité {formaterQuantite(r.capacite, r.unite as Unite)}
              {r.derniereDeclaration ? `, dernier relevé ${formaterQuantite(r.derniereDeclaration.volumeBrut, r.unite as Unite)} le ${dateHeure(r.derniereDeclaration.horodatageMesure)}` : ', aucun relevé connu'}
            </span>
          </div>
        ))}
        <div className="champ">
          <label htmlFor="d-quand">Heure du relevé</label>
          <input id="d-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
          <span className="aide">Heure du Gabon ; l’heure de l’événement est conservée même si l’envoi se fait plus tard</span>
        </div>
        </div>
      </fieldset>
      <fieldset className="carte-bloc">
        <legend>3. Vente par produit</legend>
        <div className="formulaire-ligne">
        {produits.map((p) => {
          const v = statuts[p] ?? { statut: 'disponible', motif: '' };
          return (
            <div key={p} className="champ">
              <b>{nomProduit(p)}</b>
              <div className="segments" role="radiogroup" aria-label={`Statut de vente : ${nomProduit(p)}`}>
                {(['disponible', 'non_distribuable', 'rupture_confirmee'] as const).map((s) => (
                  <button key={s} type="button" role="radio" aria-checked={v.statut === s} className={v.statut === s ? 'actif' : ''} onClick={() => setStatuts({ ...statuts, [p]: { ...v, statut: s } })}>
                    {s === 'disponible' ? 'En vente' : s === 'non_distribuable' ? 'Non distribuable' : 'Rupture'}
                  </button>
                ))}
              </div>
              {v.statut === 'non_distribuable' && (
                <div className="champ">
                  <label htmlFor={`d-motif-${p}`}>Motif</label>
                  <select id={`d-motif-${p}`} value={v.motif || 'pompe_indisponible'} onChange={(e) => setStatuts({ ...statuts, [p]: { ...v, motif: e.target.value } })}>
                    {MOTIFS_NON_DISTRIBUABLE.map((m) => (
                      <option key={m} value={m}>
                        {LIBELLES_MOTIF_NON_DISTRIBUABLE[m]}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </fieldset>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {resultat && <Message type="succes">{resultat}</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire large" disabled={envoi.isPending || incomplet}>
          {envoi.isPending ? 'Enregistrement' : 'Enregistrer la déclaration'}
        </button>
        <Link className="bouton" to={`/?station=${station.id}`}>
          Retour à ma station
        </Link>
      </div>
    </form>
  );
}

// --- Demande de réapprovisionnement ---------------------------------------------------------------------------

export function PageDemande() {
  const { station, stations, paquet, chargement, erreur } = useStationCourante();
  const [params] = useSearchParams();
  const { synchroniseur } = useHorsLigne();
  const produits = paquet?.stations?.find((s) => s.id === station?.id)?.produits ?? [];
  const [produit, setProduit] = useState(params.get('produit') ?? '');
  const [quantiteSaisie, setQuantite] = useState('');
  const [jour, setJour] = useState(() => new Date(Date.now() + 25 * 3_600_000).toISOString().slice(0, 10));
  const [urgence, setUrgence] = useState<string>('normale');
  const [commentaire, setCommentaire] = useState('');
  const [fait, setFait] = useState<string | null>(null);
  const demande = (paquet?.demandes ?? []).find((d) => d.installationId === station?.id && d.produitCode === (produit || produits[0]) && ['soumis', 'accepte', 'planifie'].includes(d.etat));
  const envoi = useMutation({
    mutationFn: async () => {
      const p = produit || produits[0]!;
      await synchroniseur.enregistrer(
        TYPES_OPERATIONS_LOT3.demandeReapprovisionnement,
        { installationId: station!.id, produitCode: p, quantite: quantiteSaisie.replace(/\s/g, '').replace(',', '.'), dateSouhaitee: `${jour}T10:00:00+01:00`, urgence, commentaire: commentaire.trim() || null },
        { versionObjet: demande?.id ?? null, resume: `Demande ${deProduit(p)}, ${station!.nom}` },
      );
    },
    onSuccess: () => {
      setFait('Demande enregistrée sur cet appareil ; elle est adressée à votre distributeur dès l’envoi.');
      setQuantite('');
      setCommentaire('');
    },
  });
  if (chargement) return <Squelette lignes={4} />;
  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!station || !stations) return <Vide>Aucune station n’est rattachée à votre affectation.</Vide>;
  if (!station.droits.demander) return <ActionReservee station={station} stations={stations} titre="Demander un réapprovisionnement" motif="La demande de réapprovisionnement est réservée à l’exploitant de la station et à son distributeur." />;
  return (
    <form
      aria-labelledby="titre-demande"
      onSubmit={(e) => {
        e.preventDefault();
        setFait(null);
        envoi.mutate();
      }}
    >
      <div className="page-tete">
        <div>
          <span className="attenue">{station.nom}</span>
          <h1 id="titre-demande">Demander un réapprovisionnement</h1>
          <p className="attenue">La demande est adressée à votre distributeur, qui l’accepte et planifie la livraison.</p>
        </div>
        <SelecteurStation stations={stations} courante={station.id} />
      </div>
      <div className="carte-bloc">
        <div className="champ">
          <label htmlFor="b-produit">Produit</label>
          <select id="b-produit" value={produit || produits[0] || ''} onChange={(e) => setProduit(e.target.value)} required>
            {produits.map((p) => (
              <option key={p} value={p}>
                {nomProduit(p)}
              </option>
            ))}
          </select>
        </div>
        {demande && (
          <Message type="avertissement">
            Une demande {demande.reference} ({quantite(demande.quantite, 'L')}) est déjà {libelleEtatBesoin(demande.etat).toLowerCase()} pour ce produit.
          </Message>
        )}
        <div className="champ">
          <label htmlFor="b-quantite">Quantité souhaitée (L)</label>
          <input id="b-quantite" inputMode="numeric" value={quantiteSaisie} onChange={(e) => setQuantite(e.target.value)} required pattern="[0-9\s]+([.,][0-9]+)?" />
        </div>
        <div className="champ">
          <label htmlFor="b-jour">Livraison souhaitée le</label>
          <input id="b-jour" type="date" value={jour} onChange={(e) => setJour(e.target.value)} required />
        </div>
        <div className="champ">
          <label htmlFor="b-urgence">Urgence</label>
          <select id="b-urgence" value={urgence} onChange={(e) => setUrgence(e.target.value)}>
            {URGENCES_BESOIN.filter((u) => u !== 'prioritaire').map((u) => (
              <option key={u} value={u}>
                {libelleUrgence(u)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="b-commentaire">Commentaire, facultatif</label>
          <input id="b-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={1000} />
        </div>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {fait && <Message type="succes">{fait}</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire large" disabled={envoi.isPending || produits.length === 0}>
          Envoyer la demande
        </button>
        <Link className="bouton" to={`/?station=${station.id}`}>
          Retour à ma station
        </Link>
      </div>
    </form>
  );
}

// --- Incident et rupture --------------------------------------------------------------------------------------

export function PageIncident() {
  const { station, stations, paquet, chargement, erreur } = useStationCourante();
  const { synchroniseur } = useHorsLigne();
  const produits = paquet?.stations?.find((s) => s.id === station?.id)?.produits ?? [];
  const [type, setType] = useState<TypeIncidentStation>('pompe');
  const [gravite, setGravite] = useState('2');
  const [produit, setProduit] = useState('');
  const [description, setDescription] = useState('');
  const [fait, setFait] = useState<string | null>(null);
  const versionStatut = paquet?.stations?.find((s) => s.id === station?.id)?.version ?? null;
  const envoi = useMutation({
    mutationFn: async () => {
      if (type === 'rupture') {
        await synchroniseur.enregistrer(TYPES_OPERATIONS_LOT3.signalementRupture, { installationId: station!.id, produitCode: produit || produits[0], commentaire: description.trim() || null }, { versionObjet: versionStatut, resume: `Rupture ${deProduit(produit || produits[0]!)}, ${station!.nom}` });
      } else {
        await synchroniseur.enregistrer(
          TYPES_OPERATIONS_LOT3.incidentStation,
          { installationId: station!.id, type, gravite: Number(gravite), produitCode: produit || null, description: description.trim() },
          { resume: `Incident : ${LIBELLES_TYPES_INCIDENT_STATION[type].toLowerCase()}, ${station!.nom}` },
        );
      }
    },
    onSuccess: () => {
      setFait(type === 'rupture' ? 'Rupture enregistrée sur cet appareil : le statut du produit passe en « rupture confirmée » et votre distributeur est prévenu à l’envoi.' : 'Incident enregistré sur cet appareil ; votre distributeur est prévenu à l’envoi.');
      setDescription('');
    },
  });
  if (chargement) return <Squelette lignes={4} />;
  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!station || !stations) return <Vide>Aucune station n’est rattachée à votre affectation.</Vide>;
  if (!station.droits.signaler) return <ActionReservee station={station} stations={stations} titre="Signaler un incident" motif="Le signalement d’un incident ou d’une rupture est réservé à l’exploitant de la station." />;
  return (
    <form
      aria-labelledby="titre-incident"
      onSubmit={(e) => {
        e.preventDefault();
        setFait(null);
        envoi.mutate();
      }}
    >
      <div className="page-tete">
        <div>
          <span className="attenue">{station.nom}</span>
          <h1 id="titre-incident">Signaler un incident</h1>
        </div>
        <SelecteurStation stations={stations} courante={station.id} />
      </div>
      <div className="carte-bloc">
        <div className="champ">
          <label htmlFor="i-type">Nature</label>
          <select id="i-type" value={type} onChange={(e) => setType(e.target.value as TypeIncidentStation)}>
            {TYPES_INCIDENT_STATION.map((t) => (
              <option key={t} value={t}>
                {LIBELLES_TYPES_INCIDENT_STATION[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="i-produit">Produit concerné{type === 'rupture' ? '' : ', facultatif'}</label>
          <select id="i-produit" value={produit} onChange={(e) => setProduit(e.target.value)} required={type === 'rupture'}>
            {type !== 'rupture' && <option value="">Aucun produit en particulier</option>}
            {produits.map((p) => (
              <option key={p} value={p}>
                {nomProduit(p)}
              </option>
            ))}
          </select>
        </div>
        {type !== 'rupture' && (
          <div className="champ">
            <label htmlFor="i-gravite">Gravité</label>
            <select id="i-gravite" value={gravite} onChange={(e) => setGravite(e.target.value)}>
              <option value="1">1, gêne mineure</option>
              <option value="2">2, distribution perturbée</option>
              <option value="3">3, distribution interrompue</option>
              <option value="4">4, danger pour les personnes ou les biens</option>
            </select>
          </div>
        )}
        <div className="champ">
          <label htmlFor="i-description">Description{type === 'rupture' ? ', facultative' : ''}</label>
          <textarea id="i-description" value={description} onChange={(e) => setDescription(e.target.value)} required={type !== 'rupture'} minLength={type === 'rupture' ? 0 : 3} maxLength={2000} rows={3} />
        </div>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {fait && <Message type="succes">{fait}</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire large" disabled={envoi.isPending}>
          {type === 'rupture' ? 'Signaler la rupture' : 'Signaler l’incident'}
        </button>
        <Link className="bouton" to={`/?station=${station.id}`}>
          Retour à ma station
        </Link>
      </div>
    </form>
  );
}

// --- Horaires -----------------------------------------------------------------------------------------------------

interface Horaires {
  version: number;
  definis: boolean;
  modifieLe: string | null;
  jours: { jour: string; ferme: boolean; continu: boolean; ouverture: string | null; fermeture: string | null; defini: boolean }[];
}

export function PageHoraires() {
  const { station, stations, chargement, erreur } = useStationCourante();
  const client = useQueryClient();
  const horaires = useQuery({ queryKey: ['station', station?.id, 'horaires'], queryFn: async () => (await api<Horaires>(`/api/v1/stations/${station!.id}/horaires`)).donnees, enabled: Boolean(station) });
  const [jours, setJours] = useState<Horaires['jours'] | null>(null);
  useEffect(() => {
    if (horaires.data) setJours(horaires.data.jours.map((j) => ({ ...j, ouverture: j.ouverture ?? (j.defini ? null : '06:00'), fermeture: j.fermeture ?? (j.defini ? null : '22:00') })));
  }, [horaires.data]);
  const envoi = useMutation({
    mutationFn: async () => (await api<Horaires>(`/api/v1/stations/${station!.id}/horaires`, { methode: 'PUT', corps: { jours: jours!.map(({ jour, ferme, continu, ouverture, fermeture }) => ({ jour, ferme, continu, ouverture, fermeture })) } })).donnees,
    onSuccess: () => void client.invalidateQueries({ queryKey: ['station', station?.id] }),
  });
  if (chargement || horaires.isLoading) return <Squelette lignes={7} />;
  if (erreur || horaires.error) return <MessageErreur erreur={erreur ?? horaires.error} />;
  if (!station || !stations || !jours) return <Vide>Aucune station n’est rattachée à votre affectation.</Vide>;
  const maj = (i: number, v: Partial<Horaires['jours'][number]>) => setJours(jours.map((j, k) => (k === i ? { ...j, ...v } : j)));
  // Sans le droit de modification, les horaires sont consultés : aucun champ ni bouton.
  const modifiable = station.droits.horaires;
  const texteJour = (j: Horaires['jours'][number]) => (j.ferme ? 'Fermée' : j.continu ? 'Ouverte 24 h sur 24' : j.defini ? `De ${j.ouverture ?? '?'} à ${j.fermeture ?? '?'}` : 'Non renseigné');
  return (
    <form
      aria-labelledby="titre-horaires"
      onSubmit={(e) => {
        e.preventDefault();
        if (modifiable) envoi.mutate();
      }}
    >
      <div className="page-tete">
        <div>
          <span className="attenue">{station.nom}</span>
          <h1 id="titre-horaires">Horaires</h1>
          <p className="attenue">
            {horaires.data?.definis ? `Version ${horaires.data.version} en vigueur depuis ${dateHeure(horaires.data.modifieLe)}. ` : 'Aucun horaire renseigné. '}
            {modifiable ? 'Chaque modification crée une nouvelle version ; l’ancienne est archivée.' : 'Consultation : la modification des horaires est réservée à l’exploitant de la station.'}
          </p>
        </div>
        <SelecteurStation stations={stations} courante={station.id} />
      </div>
      <div className="tableau-conteneur carte-bloc">
        <table className="tableau">
          <caption className="visuellement-cache">Horaires par jour</caption>
          <thead>
            <tr>
              <th scope="col">Jour</th>
              <th scope="col">Fermé</th>
              <th scope="col">24 h sur 24</th>
              <th scope="col">Ouverture</th>
              <th scope="col">Fermeture</th>
            </tr>
          </thead>
          <tbody>
            {jours.map((j, i) => (
              <tr key={j.jour}>
                <th scope="row">{j.jour.charAt(0).toUpperCase() + j.jour.slice(1)}</th>
                {!modifiable && <td colSpan={4}>{texteJour(j)}</td>}
                {modifiable && (
                  <>
                <td>
                  <input type="checkbox" aria-label={`${j.jour} : fermé`} checked={j.ferme} onChange={(e) => maj(i, { ferme: e.target.checked, continu: false })} />
                </td>
                <td>
                  <input type="checkbox" aria-label={`${j.jour} : ouvert 24 h sur 24`} checked={j.continu} disabled={j.ferme} onChange={(e) => maj(i, { continu: e.target.checked })} />
                </td>
                <td>
                  <input type="time" aria-label={`${j.jour} : ouverture`} value={j.ouverture ?? ''} disabled={j.ferme || j.continu} onChange={(e) => maj(i, { ouverture: e.target.value })} required={!j.ferme && !j.continu} />
                </td>
                <td>
                  <input type="time" aria-label={`${j.jour} : fermeture`} value={j.fermeture ?? ''} disabled={j.ferme || j.continu} onChange={(e) => maj(i, { fermeture: e.target.value })} required={!j.ferme && !j.continu} />
                </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.data && <Message type="succes">Horaires enregistrés (version {envoi.data.version}).</Message>}
      {modifiable && (
        <div className="actions">
          <button type="submit" className="bouton primaire" disabled={envoi.isPending || jours.length !== JOURS_SEMAINE.length}>
            Enregistrer les horaires
          </button>
        </div>
      )}
    </form>
  );
}

// --- Historique ---------------------------------------------------------------------------------------------------

interface EvenementHistorique {
  type: string;
  le: string;
  titre: string;
  detail: string;
  source: string | null;
}

const LIBELLES_HISTORIQUE: Record<string, string> = { statut: 'Statut', stock: 'Relevé', livraison: 'Livraison', demande: 'Demande', incident: 'Incident' };

export function PageHistoriqueStation() {
  const { station, stations, chargement, erreur } = useStationCourante();
  const [jours, setJours] = useState(30);
  const [filtre, setFiltre] = useState<string>('tout');
  const [nbLignes, setNbLignes] = useState(40);
  const historique = useQuery({ queryKey: ['station', station?.id, 'historique', jours], queryFn: async () => (await api<{ evenements: EvenementHistorique[] }>(`/api/v1/stations/${station!.id}/historique?jours=${jours}`)).donnees, enabled: Boolean(station) });
  const lignes = useMemo(() => (historique.data?.evenements ?? []).filter((e) => filtre === 'tout' || e.type === filtre), [historique.data, filtre]);
  if (chargement) return <Squelette lignes={6} />;
  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!station || !stations) return <Vide>Aucune station n’est rattachée à votre affectation.</Vide>;
  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="attenue">{station.nom}</span>
          <h1>Historique</h1>
        </div>
        <SelecteurStation stations={stations} courante={station.id} />
      </div>
      <div className="barre-filtres">
        <div className="segments" role="radiogroup" aria-label="Type d’événement">
          {['tout', 'statut', 'stock', 'livraison', 'demande', 'incident'].map((t) => (
            <button key={t} type="button" role="radio" aria-checked={filtre === t} className={filtre === t ? 'actif' : ''} onClick={() => setFiltre(t)}>
              {t === 'tout' ? 'Tout' : LIBELLES_HISTORIQUE[t]}
            </button>
          ))}
        </div>
        <div className="champ champ-etroit">
          <label htmlFor="h-jours">Période</label>
          <select id="h-jours" value={jours} onChange={(e) => setJours(Number(e.target.value))}>
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
        </div>
      </div>
      {historique.isLoading && <Squelette lignes={6} />}
      {historique.error && <MessageErreur erreur={historique.error} />}
      {historique.data && lignes.length === 0 && <Vide>Aucun événement sur la période.</Vide>}
      {lignes.length > 0 && (
        <ol className="chronologie carte-bloc">
          {lignes.slice(0, nbLignes).map((e, i) => (
            <li key={`${e.le}-${i}`}>
              <span className="etiquette">{LIBELLES_HISTORIQUE[e.type] ?? e.type}</span>
              <div>
                <b>{e.titre}</b>
                <small className="attenue">
                  {dateHeure(e.le)}, {e.detail}
                  {e.source === 'hors_ligne' ? ', saisi hors connexion' : ''}
                </small>
              </div>
            </li>
          ))}
        </ol>
      )}
      <AfficherPlus affiches={nbLignes} total={lignes.length} pas={50} libelle="événements" onPlus={() => setNbLignes(nbLignes + 50)} />
      <p className="attenue">
        {formaterNombre(Math.min(nbLignes, lignes.length))} événement{lignes.length > 1 ? 's' : ''} affiché{lignes.length > 1 ? 's' : ''} sur {formaterNombre(lignes.length)}.
      </p>
    </div>
  );
}
